import * as Schema from "effect/Schema";
import { TrimmedNonEmptyString } from "./baseSchemas";
import { ProviderKind } from "./orchestration";

export const ProviderAuthSessionId = TrimmedNonEmptyString.pipe(
  Schema.brand("ProviderAuthSessionId"),
);
export type ProviderAuthSessionId = typeof ProviderAuthSessionId.Type;

export const ProviderAuthSessionState = Schema.Union([
  Schema.Literal("starting"),
  Schema.Literal("waiting_for_code"),
  Schema.Literal("waiting_for_user"),
  Schema.Literal("completed"),
  Schema.Literal("failed"),
  Schema.Literal("canceled"),
  Schema.Literal("expired"),
]);
export type ProviderAuthSessionState = typeof ProviderAuthSessionState.Type;

export const ProviderAuthSession = Schema.Struct({
  id: ProviderAuthSessionId,
  provider: ProviderKind,
  state: ProviderAuthSessionState,
  startedAt: TrimmedNonEmptyString,
  updatedAt: TrimmedNonEmptyString,
  issue: Schema.optional(
    Schema.Literals([
      "provider_cli_missing",
      "provider_device_auth_disabled",
      "provider_auth_start_failed",
      // The surface itself cannot run a provider CLI (shared master control
      // plane or cloud-shell). Distinct from provider_cli_missing so the client
      // never offers a runtime-repair action that could not help here.
      "provider_auth_unsupported_surface",
    ]),
  ),
  deviceUrl: Schema.optional(TrimmedNonEmptyString),
  userCode: Schema.optional(TrimmedNonEmptyString),
  expiresIn: Schema.optional(TrimmedNonEmptyString),
  /**
   * Provider-issued, non-secret diagnostic code extracted from CLI output.
   * This is intentionally limited to an opaque code, never raw output.
   */
  authErrorCode: Schema.optional(TrimmedNonEmptyString),
  /** Provider-issued correlation ID, when the CLI returns one. */
  upstreamRequestId: Schema.optional(TrimmedNonEmptyString),
  message: Schema.optional(TrimmedNonEmptyString),
});
export type ProviderAuthSession = typeof ProviderAuthSession.Type;

/**
 * True when this session is blocked on a code the user must paste back.
 *
 * The two personal-provider sign-in shapes are not interchangeable. Codex
 * prints a device CODE the user types into a browser and then polls the
 * provider itself — nothing is ever written to its stdin. Claude prints an
 * authorize URL and then BLOCKS on stdin waiting for the code the browser
 * hands back, so any surface driving a Claude sign-in MUST call
 * `submitProviderAuthCode` or the process waits until the session expires.
 *
 * This predicate lives beside the session schema, not in either consumer, so
 * the local Settings flow and the cloud auth-VM flow cannot drift into
 * disagreeing about which shape a session is. That drift is exactly why cloud
 * Claude sign-in was unreachable: the auth-VM flow was built to Codex's shape
 * and never wired a submit path, so a Claude attempt sat in `waiting_for_user`
 * for the full ~10 minute budget and then reported a timeout.
 */
export function providerAuthExpectsPastedCode(
  session: ProviderAuthSession | null | undefined,
): boolean {
  return session?.state === "waiting_for_user" && session.provider === "claudeAgent";
}

export const ProviderAuthStartInput = Schema.Struct({
  provider: ProviderKind,
});
export type ProviderAuthStartInput = typeof ProviderAuthStartInput.Type;

export const ProviderAuthRuntimeRepairInput = Schema.Struct({
  provider: ProviderKind,
});
export type ProviderAuthRuntimeRepairInput = typeof ProviderAuthRuntimeRepairInput.Type;

export const ProviderAuthRuntimeRepairResult = Schema.Struct({
  provider: ProviderKind,
  status: Schema.Literals(["repaired", "unsupported", "failed"]),
  message: TrimmedNonEmptyString,
  detail: Schema.optional(Schema.String),
});
export type ProviderAuthRuntimeRepairResult = typeof ProviderAuthRuntimeRepairResult.Type;

export const ProviderAuthDisconnectInput = Schema.Struct({
  provider: ProviderKind,
});
export type ProviderAuthDisconnectInput = typeof ProviderAuthDisconnectInput.Type;

/**
 * Result of publishing a locally-connected personal Codex credential to the CMMD
 * user-scoped store (card 1359 harvest path A). `synced` = written; the rest are
 * benign no-ops (no local credential, not a personal ChatGPT credential, or no
 * CMMD session) or a transient `failed`.
 */
export const ProviderCredentialCloudSyncResult = Schema.Struct({
  status: Schema.Literals([
    "synced",
    "no_local_credential",
    "not_chatgpt",
    // Claude harvest: the local credential is not a personal Claude.ai
    // subscription login (e.g. an API-key config) — never stored.
    "not_subscription",
    "unauthenticated",
    "failed",
  ]),
  message: Schema.optional(TrimmedNonEmptyString),
});
export type ProviderCredentialCloudSyncResult = typeof ProviderCredentialCloudSyncResult.Type;

/**
 * "primary" (default) publishes to the owner's single connection row.
 * "linked" publishes to the additive linked-accounts store instead, so
 * signing in as a second ChatGPT/Claude.ai account and syncing it never
 * overwrites the account already connected (FOR-2605).
 */
export const ProviderCredentialCloudSyncInput = Schema.Struct({
  target: Schema.optional(Schema.Literals(["primary", "linked"])),
});
export type ProviderCredentialCloudSyncInput = typeof ProviderCredentialCloudSyncInput.Type;

export const ProviderAuthSessionInput = Schema.Struct({
  sessionId: ProviderAuthSessionId,
});
export type ProviderAuthSessionInput = typeof ProviderAuthSessionInput.Type;

export const ProviderAuthSubmitCodeInput = Schema.Struct({
  sessionId: ProviderAuthSessionId,
  code: TrimmedNonEmptyString,
});
export type ProviderAuthSubmitCodeInput = typeof ProviderAuthSubmitCodeInput.Type;
